import React from 'react'
import classNames from 'classnames'

import { mq } from 'marketing-site/src/library/utils'
import {
  IHeaderNavigationDesktop,
  isHeaderNavigationMenuDescription,
  isHeaderNavigationMenu,
  isHeaderNavigationMenuSection,
  isHeaderNavigationSolutions,
  isHeaderNavigationSpotlight,
} from '..'
import { HeaderNavigationMenuSection } from './HeaderNavigationMenuSection'
import { HeaderNavigationMenuDescription } from './HeaderNavigationMenuDescription'
import { HeaderNavigationSolutions } from './HeaderNavigationSolutions'
import { HeaderNavigationSpotlight } from './HeaderNavigationSpotlight'
import { Text } from 'marketing-site/src/library/elements/Text'
import { Icon } from 'marketing-site/src/mvp/library/elements/Icon'

export const HeaderNavigationDesktop = ({
  contentType = 'flex',
  theme = 'light',
  navigationElements,
}: IHeaderNavigationDesktop): React.ReactElement => {
  const classes = classNames('navigation-menu', {
    [`navigation-menu--theme-${theme}`]: theme,
  })

  const removeOpen = (remove = true) => {
    if (remove) {
      const navigationItems = document.querySelectorAll('.navigation-submenu')
      navigationItems.forEach((navigationItem) => {
        navigationItem.classList.remove('open')
      })
    }
  }

  const showSubMenu = (e: any) => {
    if (e.keyCode === 32 || e.keyCode === 13) {
      const siblingElement = e.target.nextElementSibling
      removeOpen()
      if (siblingElement) siblingElement.classList.add('open')
    }
  }

  return (
    <>
      <div className={classes}>
        <ul className="navigation-menu__inner">
          {navigationElements.map((item) => {
            return (
              <li
                className="navigation-item"
                role="presentation"
                key={item.title}
                data-testid="navigation-item"
                onKeyDown={showSubMenu}
                onFocus={(e) => removeOpen(e.target.classList.contains('navigation-item-link'))}
              >
                {isHeaderNavigationMenu(item) ? (
                  <>
                    <button className="navigation-item-link">
                      <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="body-bold">
                        {item.title}
                      </Text>
                      <Icon icon="caret-down-bold" />
                    </button>
                    <div className="navigation-submenu">
                      <div className="navigation-submenu__inner">
                        <div className="navigation-submenu__content">
                          {item.content.map((element, i) => {
                            if (isHeaderNavigationMenuDescription(element)) {
                              return (
                                <React.Fragment key={i}>
                                  <HeaderNavigationMenuDescription
                                    {...element}
                                    contentType={contentType}
                                  />
                                  <div className="menu-description-divider"></div>
                                </React.Fragment>
                              )
                            }
                          })}
                          {item.content.map((element, i) => {
                            if (isHeaderNavigationMenuSection(element)) {
                              return (
                                <HeaderNavigationMenuSection
                                  key={i}
                                  {...element}
                                  contentType={contentType}
                                  theme={theme}
                                />
                              )
                            } else if (isHeaderNavigationSolutions(element)) {
                              return (
                                <HeaderNavigationSolutions
                                  key={i}
                                  {...element}
                                  contentType={contentType}
                                />
                              )
                            }
                          })}
                        </div>
                        <div className="navigation-submenu__sidebar">
                          {item.sidebar.map((element, i) => {
                            if (isHeaderNavigationSpotlight(element)) {
                              return (
                                <div key={i} className="navigation-submenu__sidebar--spotlight">
                                  <HeaderNavigationSpotlight
                                    {...element}
                                    contentType={contentType}
                                    theme={theme}
                                  />
                                </div>
                              )
                            } else if (isHeaderNavigationMenuSection(element)) {
                              return (
                                <HeaderNavigationMenuSection
                                  key={i}
                                  {...element}
                                  contentType={contentType}
                                  theme={theme}
                                />
                              )
                            }
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <a className="navigation-item-link" href={item.url}>
                    <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="body-bold">
                      {item.title}
                    </Text>
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      <style jsx>{`
        .navigation-menu {
          display: none;
        }

        @media (${mq.desktop}) {
          .navigation-menu {
            display: block;
            margin-left: 1.25rem;

            &__inner {
              display: flex;
              gap: 1.25rem;
            }

            &--theme-light {
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              .navigation-item-link {
                color: inherit;
              }

              &:hover {
                .navigation-item:not(:hover) {
                  .navigation-item-link {
                    color: var(--neutral-500);
                  }
                }
              }

              .navigation-submenu {
                background-color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};
                border-top: 1px solid
                  ${contentType === 'flex' ? 'var(--stone)' : 'var(--mvp-stone)'};
                color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

                &__sidebar {
                  background-color: ${contentType === 'flex' ? 'var(--stone)' : 'var(--mvp-stone)'};

                  &::before {
                    background-color: ${contentType === 'flex'
                      ? 'var(--stone)'
                      : 'var(--mvp-stone)'};
                  }
                }
              }
            }

            &--theme-dark,
            &--theme-resolution-bot {
              color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};

              .navigation-item-link {
                color: inherit;
              }

              &:hover {
                .navigation-item:not(:hover) {
                  .navigation-item-link {
                    color: var(--neutral-400);
                  }
                }
              }

              .navigation-submenu {
                color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};
              }
            }

            &--theme-dark {
              .navigation-submenu {
                background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
                border-top: 1px solid var(--neutral-800);
                border-bottom: 1px solid var(--neutral-800);

                &__sidebar {
                  background-color: var(--neutral-800);

                  &::before {
                    background-color: var(--neutral-800);
                  }
                }
              }
            }

            &--theme-resolution-bot {
              .navigation-submenu {
                background-color: black;
                border-top: 1px solid var(--neutral-800);
                border-bottom: 1px solid var(--neutral-800);

                &__sidebar {
                  background-color: var(--neutral-800);

                  &::before {
                    background-color: var(--neutral-800);
                  }
                }
              }
            }
          }

          .navigation-item {
            display: flex;
            gap: 1rem;

            &:hover {
              &::before {
                content: '';
                position: absolute;
                top: calc(100% - 1.5rem);
                left: 0;
                right: 0;
                height: 1.5rem;
              }

              .navigation-item-link > :global(.icon > path) {
                transform: rotateX(180deg);
              }

              .navigation-submenu {
                opacity: 1;
                visibility: visible;
                transition: opacity 0.4s, visibility 0s, background-color 0.32s, border 0.32s;
              }
            }
          }

          .navigation-item-link {
            display: flex;
            gap: 0.25rem;
            align-items: center;
            padding: 1rem 0;
            text-decoration: none;
            transition: color 0.32s;
            cursor: pointer;

            & > :global(.icon > path) {
              transform-origin: center;
              transition: transform 0.32s ease-in-out;
            }

            :global(span) {
              color: inherit;
            }
          }

          .navigation-submenu {
            overflow: hidden;
            width: 100%;
            padding-left: 2.5rem;
            padding-right: 2.5rem;
            position: absolute;
            top: 100%;
            left: 50%;
            box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.12);
            transform: translateX(-50%);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.32s, visibility 0s 0.32s, background-color 0.32s, border 0.32s;
            z-index: 111;

            &.open {
              & {
                opacity: 1;
                visibility: visible;
                transition: opacity 0.4s, visibility 0s;
              }
              :global(.icon > path) {
                transform: rotateX(180deg);
              }
            }

            &--active {
              opacity: 1;
            }

            &__inner {
              display: flex;
              gap: 2rem;
              max-width: ${contentType === 'flex' ? '1290px' : '77.5rem'};
              margin: 0 auto;
            }

            &__content {
              display: flex;
              gap: 2rem;
              padding-top: 3rem;
              padding-bottom: 3rem;
            }

            &__sidebar {
              flex: 1 0 25%;
              display: flex;
              flex-direction: column;
              gap: 2.5rem;
              padding: 3rem 0 3rem 2rem;
              position: relative;
              transition: background-color 0.32s;

              &::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                right: -100vw;
                left: 0;
                transition: background-color 0.32s;
                z-index: -1;
              }

              :global(.header-navigation-section__items-column) {
                gap: 1rem;
              }

              :global(.cta-link) {
                &:focus,
                &:hover {
                  text-decoration: underline;
                }
              }
            }
          }

          .menu-description-divider {
            margin: 1.5rem 0;
          }
        }

        @media (${mq.desktopLg}) {
          .navigation-menu {
            margin-left: 5.25rem;

            &__inner {
              gap: 2rem;
            }
          }
        }
      `}</style>
    </>
  )
}
